import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ClipboardText, Plus, PencilSimple, Trash, UploadSimple, DownloadSimple,
  CheckCircle, ArrowRight, CaretRight, CaretDown, Buildings, Stack, Package,
  DotsThreeVertical, BookOpen, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);
const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const NODE_ICON = { block: Buildings, floor: Stack, item: Package };
const NODE_TINT = {
  block: "text-[#3730A3] bg-[#E0E7FF]",
  floor: "text-[#B06000] bg-[#FEF7E0]",
  item: "text-[#137333] bg-[#E6F4EA]",
};

export default function BOQManagement() {
  const { activeProjectId, activeProject } = useProject();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("hierarchy"); // 'hierarchy' | 'master-descriptions'
  const [tree, setTree] = useState({ nodes: [], totals: { total_qty: 0, total_amount: 0, item_count: 0 } });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());

  const [nodeDialog, setNodeDialog] = useState(null); // { mode: 'create'|'edit', node?, parent?, node_type }
  const [importOpen, setImportOpen] = useState(false);
  const [quickPO, setQuickPO] = useState(null); // BOQ item node
  const [rateReq, setRateReq] = useState(null); // BOQ item node

  const load = async () => {
    if (!activeProjectId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/boq/tree", { params: { project_id: activeProjectId } });
      setTree(data);
      // Auto-expand top-level blocks so users see structure without clicks
      setExpanded((prev) => {
        const next = new Set(prev);
        (data.nodes || []).forEach((n) => n.node_type !== "item" && next.add(n.id));
        return next;
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeProjectId]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const expandAll = () => {
    const all = new Set();
    const walk = (nodes) => nodes.forEach((n) => { if (n.node_type !== "item") { all.add(n.id); walk(n.children || []); } });
    walk(tree.nodes || []);
    setExpanded(all);
  };
  const collapseAll = () => setExpanded(new Set());

  const handleDeleteNode = async (node) => {
    const hasChildren = (node.children || []).length > 0;
    const cascade = hasChildren;
    const label = `${node.node_type} "${node.name}"${hasChildren ? ` (and its ${node.children.length} child node(s))` : ""}`;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/boq/nodes/${node.id}${cascade ? "?cascade=true" : ""}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openCreate = (parent, defaultType) => {
    setNodeDialog({ mode: "create", parent, node_type: defaultType });
  };
  const openEdit = (node) => setNodeDialog({ mode: "edit", node });

  const stats = tree.totals || { total_qty: 0, total_amount: 0, item_count: 0 };

  return (
    <div className="p-8 sm:p-10 max-w-[1500px] mx-auto" data-testid="boq-management-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2 flex items-center gap-2">
            {activeProject && (
              <>
                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: activeProject.color || "#0047FF" }} />
                <span>{activeProject.name}</span>
                <span className="text-neutral-300">·</span>
              </>
            )}
            <span>Inventory · Bill of Quantities</span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <ClipboardText size={30} className="text-primary" weight="fill" /> Bill of Quantities
          </h1>
          <p className="text-sm text-neutral-500 mt-2">
            Structure your BOQ by Block → Floor → Item. Each parent auto-rolls up quantity + budget from its descendants.
          </p>
        </div>
        {tab === "hierarchy" && (
        <div className="flex flex-wrap gap-2">
          <a
            href={`${API}/boq/template.xlsx`}
            className="inline-flex items-center gap-1.5 h-10 px-3 text-sm border border-neutral-300 rounded-sm hover:border-primary hover:text-primary transition-colors"
            data-testid="boq-download-template"
            target="_blank"
            rel="noopener noreferrer"
          >
            <DownloadSimple size={14} /> Template (.xlsx)
          </a>
          <a
            href={`${API}/boq/template.csv`}
            className="inline-flex items-center gap-1.5 h-10 px-3 text-sm border border-neutral-300 rounded-sm hover:border-primary hover:text-primary transition-colors"
            data-testid="boq-download-template-csv"
            target="_blank"
            rel="noopener noreferrer"
          >
            <DownloadSimple size={14} /> Template (.csv)
          </a>
          <Button variant="outline" className="rounded-sm h-10 gap-1.5" onClick={() => setImportOpen(true)} data-testid="boq-import-btn">
            <UploadSimple size={14} /> Import Excel
          </Button>
          <Button className="rounded-sm h-10 bg-primary hover:bg-[#0039CC] gap-1.5" onClick={() => openCreate(null, "block")} data-testid="boq-add-block-btn">
            <Plus size={14} weight="bold" /> Add Block
          </Button>
        </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-200 mb-6 flex gap-1" data-testid="boq-tabs">
        <TabButton active={tab === "hierarchy"} onClick={() => setTab("hierarchy")} icon={<Buildings size={14} weight="fill" />} label="Hierarchy" testId="boq-tab-hierarchy" />
        {isAdmin && (
          <TabButton active={tab === "master-descriptions"} onClick={() => setTab("master-descriptions")} icon={<BookOpen size={14} weight="fill" />} label="Master Descriptions" testId="boq-tab-master-descriptions" />
        )}
      </div>

      {tab === "master-descriptions" ? (
        <MasterDescriptionsPanel projectId={activeProjectId} />
      ) : (
      <>
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <SummaryCard label="Total Items" value={stats.item_count} accent="text-neutral-900" />
        <SummaryCard label="Total BOQ Qty" value={fmt(stats.total_qty)} accent="text-neutral-900" />
        <SummaryCard label="Original Budget" value={fmtINR(stats.total_amount)} accent="text-primary" />
      </div>

      {/* Tree toolbar */}
      <div className="flex items-center justify-between mb-3 text-xs">
        <div className="flex gap-2">
          <button className="text-neutral-500 hover:text-primary underline" onClick={expandAll} data-testid="boq-expand-all">Expand all</button>
          <span className="text-neutral-300">|</span>
          <button className="text-neutral-500 hover:text-primary underline" onClick={collapseAll} data-testid="boq-collapse-all">Collapse all</button>
        </div>
        <div className="text-neutral-400 tabular-nums">{tree.nodes?.length || 0} root block(s)</div>
      </div>

      {/* Tree */}
      <div className="bg-white border border-neutral-200 rounded-sm" data-testid="boq-tree-root">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-neutral-200 bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500 sticky top-0">
          <div className="col-span-6">Name</div>
          <div className="col-span-2 text-right">Qty</div>
          <div className="col-span-2 text-right">Amount / Rate</div>
          <div className="col-span-1 text-center">Progress</div>
          <div className="col-span-1 text-right pr-2">Actions</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-neutral-400 text-sm">Loading tree…</div>
        ) : (tree.nodes || []).length === 0 ? (
          <div className="p-16 text-center">
            <Buildings size={40} className="mx-auto text-neutral-300 mb-3" />
            <div className="text-sm text-neutral-500 mb-3">No BOQ nodes yet for this project.</div>
            <Button className="rounded-sm bg-primary hover:bg-[#0039CC] gap-1" onClick={() => openCreate(null, "block")}>
              <Plus size={14} /> Add your first Block
            </Button>
          </div>
        ) : (
          <div>
            {flattenTree(tree.nodes || [], expanded).map(({ node, depth }) => (
              <TreeRow
                key={node.id}
                node={node}
                depth={depth}
                expanded={expanded}
                onToggle={toggle}
                onAddChild={openCreate}
                onEdit={openEdit}
                onDelete={handleDeleteNode}
                onRaisePO={(n) => setQuickPO(n)}
                onRequestRateRevision={(n) => setRateReq(n)}
              />
            ))}
          </div>
        )}
      </div>
      </>
      )}

      {nodeDialog && (
        <NodeDialog
          projectId={activeProjectId}
          config={nodeDialog}
          allNodes={tree.nodes || []}
          onClose={() => setNodeDialog(null)}
          onSaved={async () => { setNodeDialog(null); await load(); }}
        />
      )}

      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        projectId={activeProjectId}
        onImported={load}
      />
      {quickPO && (
        <QuickPOFromBOQDialog item={quickPO} onClose={() => setQuickPO(null)} onCreated={() => { setQuickPO(null); load(); }} />
      )}
      {rateReq && (
        <RateRevisionDialog item={rateReq} onClose={() => setRateReq(null)} onSubmitted={() => setRateReq(null)} />
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className="border border-neutral-200 bg-white rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`font-display text-2xl font-bold tabular-nums mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

function flattenTree(roots, expandedSet) {
  const out = [];
  const stack = [];
  for (let i = roots.length - 1; i >= 0; i--) stack.push({ node: roots[i], depth: 0 });
  while (stack.length) {
    const cur = stack.pop();
    out.push(cur);
    const n = cur.node;
    if (n.node_type !== "item" && expandedSet.has(n.id) && (n.children || []).length) {
      for (let i = n.children.length - 1; i >= 0; i--) stack.push({ node: n.children[i], depth: cur.depth + 1 });
    }
  }
  return out;
}

function TreeRow({ node, depth, expanded, onToggle, onAddChild, onEdit, onDelete, onRaisePO, onRequestRateRevision }) {
  const isOpen = expanded.has(node.id);
  const Icon = NODE_ICON[node.node_type] || Package;
  const tint = NODE_TINT[node.node_type] || NODE_TINT.item;
  const isItem = node.node_type === "item";
  const [menuOpen, setMenuOpen] = useState(false);

  const qty = isItem ? node.total_qty : node.rolled_up_qty;
  const amount = isItem ? node.total_amount : node.rolled_up_amount;
  const rate = isItem ? node.rate : null;
  const percent = isItem ? (node.percent_ordered ?? 0) : null;
  const over = isItem ? node.over_budget : false;

  const canAddChildType =
    node.node_type === "block" ? ["floor", "item"] :
    node.node_type === "floor" ? ["item"] : [];

  const rowClass = "grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-neutral-100 hover:bg-neutral-50/70 transition-colors " + (over ? "bg-[#FCE8E6]/40" : "");
  const barColor = over ? "bg-[#C5221F]" : (percent >= 90 ? "bg-[#B06000]" : "bg-[#137333]");
  const indentStyle = { paddingLeft: (depth * 20) + "px" };

  let subLine = null;
  if (isItem) {
    const parts = [];
    if (node.category) parts.push(node.category);
    if (node.unit) parts.push(node.unit);
    if (node.description) parts.push(node.description);
    subLine = parts.join(" · ");
  } else {
    subLine = (node.item_count ?? 0) + " item(s) · " + ((node.children || []).length) + " direct child(ren)";
  }

  return (
    <div className={rowClass} data-testid={"boq-tree-node-" + node.id}>
      <div className="col-span-6 flex items-center gap-2 min-w-0" style={indentStyle}>
        {!isItem ? (
          <button
            onClick={() => onToggle(node.id)}
            className="w-5 h-5 flex-shrink-0 text-neutral-400 hover:text-primary"
            data-testid={"boq-toggle-" + node.id}
            aria-label={isOpen ? "Collapse" : "Expand"}
          >
            {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
          </button>
        ) : <div className="w-5" />}
        <div className={"inline-flex items-center justify-center w-6 h-6 rounded-sm flex-shrink-0 " + tint}>
          <Icon size={13} weight="fill" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-sm text-neutral-900 truncate">
            {isItem ? (
              <Link
                to={"/boq/item/" + node.id}
                className="hover:text-primary hover:underline underline-offset-2"
                data-testid={"boq-item-link-" + node.id}
              >
                {node.name || "—"}
              </Link>
            ) : (
              node.name || "—"
            )}
          </div>
          <div className="text-[11px] text-neutral-500 truncate">{subLine}</div>
        </div>
      </div>
      <div className="col-span-2 text-right tabular-nums text-sm text-neutral-900 self-center">
        {qty != null ? fmt(qty) : "—"}
        {isItem && node.unit ? <span className="text-neutral-400 text-[10px] ml-1">{node.unit}</span> : null}
      </div>
      <div className="col-span-2 text-right tabular-nums text-sm self-center">
        {amount ? <span className="text-neutral-900">{fmtINR(amount)}</span> : rate ? <span className="text-neutral-500">@{fmtINR(rate)}</span> : <span className="text-neutral-300">—</span>}
      </div>
      <div className="col-span-1 self-center">
        {isItem ? (
          <div className="w-full">
            <div className="h-1.5 bg-neutral-100 overflow-hidden rounded-sm">
              <div className={"h-full transition-all " + barColor} style={{ width: Math.min(percent || 0, 100) + "%" }} />
            </div>
            <div className="text-[9px] text-center text-neutral-500 mt-0.5 tabular-nums">{percent?.toFixed?.(0) ?? 0}%</div>
          </div>
        ) : <div className="text-[10px] text-neutral-400 text-center">—</div>}
      </div>
      <div className="col-span-1 flex items-center justify-end gap-1 self-center relative">
        {isItem && onRaisePO && (
          <button
            className="text-[10px] font-medium px-2 py-1 rounded-sm bg-primary/10 text-primary hover:bg-primary hover:text-white transition-colors"
            title="Raise a new PO against this BOQ item"
            onClick={() => onRaisePO(node)}
            data-testid={"boq-raise-po-" + node.id}
          >
            + PO
          </button>
        )}
        {isItem && onRequestRateRevision && (
          <button
            className="text-[10px] font-medium px-2 py-1 rounded-sm border border-[#B06000] text-[#B06000] hover:bg-[#FEF7E0] transition-colors"
            title="Propose a rate change on this BOQ item"
            onClick={() => onRequestRateRevision(node)}
            data-testid={"boq-request-rate-" + node.id}
          >
            Rate ↑↓
          </button>
        )}
        {canAddChildType.map((t) => (
          <button
            key={t}
            className="p-1 text-neutral-400 hover:text-primary"
            title={"Add " + t}
            onClick={() => onAddChild(node, t)}
            data-testid={"boq-add-child-" + t + "-" + node.id}
          >
            <Plus size={13} weight="bold" />
          </button>
        ))}
        <button className="p-1 text-neutral-400 hover:text-primary" onClick={() => setMenuOpen(v => !v)} data-testid={"boq-menu-" + node.id}>
          <DotsThreeVertical size={14} weight="bold" />
        </button>
        {menuOpen ? (
          <div className="absolute right-0 top-7 z-10 bg-white border border-neutral-200 rounded-sm shadow-lg text-xs min-w-[120px]">
            <button
              onClick={() => { setMenuOpen(false); onEdit(node); }}
              className="flex items-center gap-2 px-3 py-1.5 w-full hover:bg-neutral-50"
              data-testid={"boq-edit-" + node.id}
            >
              <PencilSimple size={12} /> Edit
            </button>
            <button
              onClick={() => { setMenuOpen(false); onDelete(node); }}
              className="flex items-center gap-2 px-3 py-1.5 w-full hover:bg-[#FCE8E6] text-[#C5221F]"
              data-testid={"boq-delete-" + node.id}
            >
              <Trash size={12} /> Delete
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------- Node create/edit dialog ------------------------- */
function NodeDialog({ projectId, config, onClose, onSaved }) {
  const isEdit = config.mode === "edit";
  const nodeType = isEdit ? config.node.node_type : config.node_type;
  const parent = config.parent;
  const [form, setForm] = useState(() => isEdit ? {
    name: config.node.name || "",
    unit: config.node.unit || "",
    total_qty: config.node.total_qty ?? "",
    description: config.node.description || "",
    rate: config.node.rate ?? "",
    total_amount: config.node.total_amount ?? "",
  } : { name: "", unit: "", total_qty: "", description: "", rate: "", total_amount: "" });
  const [saving, setSaving] = useState(false);

  const title = isEdit
    ? `Edit ${nodeType[0].toUpperCase() + nodeType.slice(1)}`
    : `Add ${nodeType[0].toUpperCase() + nodeType.slice(1)}${parent ? ` under "${parent.name}"` : ""}`;

  const submit = async () => {
    if (!form.name.trim() && nodeType !== "item") {
      toast.error("Name is required");
      return;
    }
    if (nodeType === "item") {
      if (!form.name?.trim() || !form.unit?.trim() || !form.total_qty) {
        toast.error("Name, Unit and Total Qty are required for items");
        return;
      }
    }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name || undefined };
        if (nodeType === "item") {
          payload.unit = form.unit;
          payload.total_qty = Number(form.total_qty);
          payload.description = form.description;
          payload.rate = form.rate ? Number(form.rate) : null;
          payload.total_amount = form.total_amount ? Number(form.total_amount) : null;
        }
        await api.put(`/boq/nodes/${config.node.id}`, payload);
        toast.success("Updated");
      } else {
        const payload = {
          project_id: projectId,
          node_type: nodeType,
          name: form.name,
          parent_id: parent?.id || null,
        };
        if (nodeType === "item") {
          payload.unit = form.unit;
          payload.total_qty = Number(form.total_qty);
          payload.description = form.description;
          payload.rate = form.rate ? Number(form.rate) : null;
          payload.total_amount = form.total_amount ? Number(form.total_amount) : null;
        }
        await api.post("/boq/nodes", payload);
        toast.success(`${nodeType[0].toUpperCase() + nodeType.slice(1)} created`);
      }
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="boq-node-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{title}</DialogTitle>
          <DialogDescription>
            {parent && !isEdit
              ? <>Parent: <span className="text-neutral-800">{parent.name}</span></>
              : (isEdit ? "Update this node's details." : "Add a new node at the project root.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {nodeType !== "item" ? (
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={nodeType === "block" ? "Tower A, Common Areas…" : "Ground Floor, 1st Floor…"} className="rounded-sm h-10" data-testid="boq-node-name" />
            </div>
          ) : (
            <>
              {!isEdit && (
                <MasterDescriptionPicker
                  projectId={projectId}
                  onPick={(md) => setForm((f) => ({
                    ...f,
                    name: f.name || md.description,
                    unit: md.unit || f.unit,
                    rate: md.rate ?? f.rate,
                    description: md.description || f.description,
                  }))}
                />
              )}
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Item Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Cement OPC 53, 16mm Rebar…" className="rounded-sm h-10" data-testid="boq-node-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-neutral-500">Unit *</Label>
                  <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="bags, kg, sqft…" className="rounded-sm h-10" data-testid="boq-node-unit" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-neutral-500">Total Qty *</Label>
                  <Input type="number" step="0.01" value={form.total_qty} onChange={(e) => setForm({ ...form, total_qty: e.target.value })} className="rounded-sm h-10 tabular-nums" data-testid="boq-node-total-qty" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-neutral-500">Rate</Label>
                  <Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="rounded-sm h-10 tabular-nums" data-testid="boq-node-rate" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-neutral-500">Total Amount (optional; auto-derived from qty × rate)</Label>
                  <Input type="number" step="0.01" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} className="rounded-sm h-10 tabular-nums" data-testid="boq-node-total-amount" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-neutral-500">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm min-h-[70px]" data-testid="boq-node-desc" />
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="rounded-sm bg-primary hover:bg-[#0039CC]" onClick={submit} disabled={saving} data-testid="boq-node-save">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------- Import wizard ------------------------- */
function ImportDialog({ open, onOpenChange, projectId, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [headerRow, setHeaderRow] = useState(1);
  const [mapping, setMapping] = useState({
    block: "Block", floor: "Floor",
    category: "Category", unit: "Unit", total_qty: "Total Qty",
    description: "Description", rate: "Rate", total_amount: "Total Amount",
  });
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setStep(1); setFile(null); setSheets([]); setSelectedSheet(""); setHeaderRow(1);
      setMapping({
        block: "Block", floor: "Floor",
        category: "Category", unit: "Unit", total_qty: "Total Qty",
        description: "Description", rate: "Rate", total_amount: "Total Amount",
      });
      setReplace(false); setResult(null); setBusy(false);
    }
  }, [open]);

  const preview = async () => {
    if (!file) { toast.error("Choose an .xlsx file first"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/boq/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      setSheets(data.sheets || []);
      const firstNonEmpty = (data.sheets || []).find((s) => s.raw_rows?.length > 0);
      setSelectedSheet(firstNonEmpty?.name || "");
      setStep(2);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const currentSheet = sheets.find((s) => s.name === selectedSheet);
  const headers = currentSheet?.raw_rows?.[headerRow - 1] || [];
  const nonEmptyHeaders = headers.filter((h) => h && h.trim());

  const doImport = async () => {
    if (!mapping.category || !mapping.unit || !mapping.total_qty) {
      toast.error("Map Category, Unit and Total Quantity columns first");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("project_id", projectId);
      form.append("sheet", selectedSheet);
      form.append("header_row", headerRow);
      form.append("mapping", JSON.stringify(mapping));
      form.append("replace", replace ? "true" : "false");
      const { data } = await api.post("/boq/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.inserted} items imported`);
      if (data.inserted > 0) await onImported?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-4xl" data-testid="boq-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><UploadSimple size={18} /> Import BOQ from Excel</DialogTitle>
          <DialogDescription>
            Upload an .xlsx, pick sheet + header row, then map columns. Include <span className="font-medium">Block</span> / <span className="font-medium">Floor</span> columns to build the hierarchy automatically — or use section-header rows like <code className="text-[11px] bg-neutral-100 px-1">Block: Tower A</code>.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-neutral-200 rounded-sm p-8 text-center hover:border-primary transition-colors">
              <UploadSimple size={36} className="mx-auto text-neutral-400 mb-3" />
              <input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" id="boq-file-input" data-testid="boq-import-file" />
              <label htmlFor="boq-file-input" className="cursor-pointer text-primary font-medium underline">
                {file ? file.name : "Click to select .xlsx"}
              </label>
              <div className="text-xs text-neutral-400 mt-1">Max 15 MB · Don&apos;t have one? <a href={`${API}/boq/template.xlsx`} target="_blank" rel="noopener noreferrer" className="text-primary underline">Download sample template</a></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button className="rounded-sm bg-primary hover:bg-[#0039CC] gap-1.5" disabled={!file || busy} onClick={preview} data-testid="boq-import-next">
                {busy ? "Reading…" : <>Next <ArrowRight size={14} weight="bold" /></>}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && !result && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Sheet</Label>
                <Select value={selectedSheet} onValueChange={(v) => { setSelectedSheet(v); setHeaderRow(1); }}>
                  <SelectTrigger className="rounded-sm h-10" data-testid="boq-import-sheet"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {sheets.map((s) => <SelectItem key={s.name} value={s.name}>{s.name} · {s.row_count} rows</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Header Row (1-{currentSheet?.raw_rows?.length || 10})</Label>
                <Input type="number" min={1} max={currentSheet?.raw_rows?.length || 10} value={headerRow} onChange={(e) => setHeaderRow(Number(e.target.value) || 1)} className="rounded-sm h-10 tabular-nums" data-testid="boq-import-header-row" />
              </div>
            </div>

            {currentSheet?.raw_rows?.length > 0 && (
              <div className="border border-neutral-200 rounded-sm overflow-x-auto">
                <table className="w-full text-[11px]">
                  <tbody>
                    {currentSheet.raw_rows.map((row, ri) => (
                      <tr key={ri} className={ri + 1 === headerRow ? "bg-primary/10 font-medium" : "hover:bg-neutral-50"}>
                        <td className="px-2 py-1 text-neutral-400 border-r border-neutral-100 w-8 text-right tabular-nums">{ri + 1}</td>
                        {row.map((c, ci) => (
                          <td key={ci} className="px-2 py-1 border-r border-neutral-100 truncate max-w-[160px]">{c || <span className="text-neutral-300">—</span>}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div>
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Column Mapping</div>
              <div className="grid grid-cols-2 gap-3">
                <MapField label="Block" value={mapping.block} onChange={(v) => setMapping({ ...mapping, block: v })} headers={nonEmptyHeaders} testId="boq-map-block" />
                <MapField label="Floor" value={mapping.floor} onChange={(v) => setMapping({ ...mapping, floor: v })} headers={nonEmptyHeaders} testId="boq-map-floor" />
                <MapField label="Category *" value={mapping.category} onChange={(v) => setMapping({ ...mapping, category: v })} headers={nonEmptyHeaders} testId="boq-map-category" />
                <MapField label="Unit *" value={mapping.unit} onChange={(v) => setMapping({ ...mapping, unit: v })} headers={nonEmptyHeaders} testId="boq-map-unit" />
                <MapField label="Total Quantity *" value={mapping.total_qty} onChange={(v) => setMapping({ ...mapping, total_qty: v })} headers={nonEmptyHeaders} testId="boq-map-qty" />
                <MapField label="Description" value={mapping.description} onChange={(v) => setMapping({ ...mapping, description: v })} headers={nonEmptyHeaders} testId="boq-map-desc" />
                <MapField label="Rate" value={mapping.rate} onChange={(v) => setMapping({ ...mapping, rate: v })} headers={nonEmptyHeaders} testId="boq-map-rate" />
                <MapField label="Total Amount" value={mapping.total_amount} onChange={(v) => setMapping({ ...mapping, total_amount: v })} headers={nonEmptyHeaders} testId="boq-map-amount" />
              </div>
              <div className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                Tip: if your sheet doesn&apos;t have Block/Floor columns but uses rows like <code className="bg-neutral-100 px-1">Block: Tower A</code> or <code className="bg-neutral-100 px-1">Floor: Ground</code> to group items, you can leave those mappings empty — we&apos;ll auto-detect them.
              </div>
            </div>

            <label className="flex items-center gap-2 text-xs text-neutral-600">
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="boq-import-replace" />
              Replace all existing BOQ items in this project first (destructive)
            </label>

            <DialogFooter>
              <Button variant="ghost" className="rounded-sm" onClick={() => setStep(1)}>Back</Button>
              <Button className="rounded-sm bg-primary hover:bg-[#0039CC] gap-1.5" disabled={busy} onClick={doImport} data-testid="boq-import-confirm">
                {busy ? "Importing…" : "Import"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && result && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-[#137333] text-sm">
              <CheckCircle size={22} weight="fill" />
              <div>
                <div className="font-medium">Import complete</div>
                <div className="text-xs text-neutral-500">
                  {result.inserted} inserted · {result.skipped_invalid} skipped invalid
                  {result.replace ? " · previous BOQ replaced" : ""}
                </div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="text-[11px] text-[#B06000] bg-[#FEF7E0] border border-[#FCE39E] p-2 rounded-sm">
                {result.errors.map((e, i) => <div key={i}>· {e}</div>)}
              </div>
            )}
            <DialogFooter>
              <Button className="rounded-sm bg-primary hover:bg-[#0039CC]" onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MapField({ label, value, onChange, headers, testId }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={value || ""} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
        <SelectTrigger className="rounded-sm h-9" data-testid={testId}><SelectValue placeholder="— none —" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— none —</SelectItem>
          {headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

/* -------- Reusable progress bar used by Stock page -------- */
export function BOQProgress({ percent, overBudget }) {
  const p = Math.min(percent || 0, 100);
  const color = overBudget ? "bg-[#C5221F]" : p >= 90 ? "bg-[#B06000]" : "bg-[#137333]";
  return (
    <div className="w-full">
      <div className="h-1.5 bg-neutral-100 overflow-hidden rounded-sm">
        <div className={"h-full transition-all " + color} style={{ width: p + "%" }} />
      </div>
      <div className="text-[9px] text-center text-neutral-500 mt-0.5 tabular-nums">{(percent ?? 0).toFixed?.(0) ?? 0}%</div>
    </div>
  );
}


/* ============================================================
 * Tab button
 * ============================================================ */
function TabButton({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={
        "px-4 py-2.5 -mb-[1px] border-b-2 flex items-center gap-2 text-sm font-medium transition-colors " +
        (active
          ? "border-primary text-primary"
          : "border-transparent text-neutral-500 hover:text-neutral-900")
      }
    >
      {icon}
      {label}
    </button>
  );
}

/* ============================================================
 * Master Descriptions Panel (admin-only tab)
 * ============================================================ */
function MasterDescriptionsPanel({ projectId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = { project_id: projectId, page, page_size: pageSize };
      if (search) params.search = search;
      const { data } = await api.get("/master-descriptions", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, page]);

  const onSearchSubmit = (e) => {
    e?.preventDefault?.();
    setPage(1);
    load();
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.description}" from Master Descriptions?`)) return;
    try {
      await api.delete(`/master-descriptions/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="master-descriptions-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <form onSubmit={onSearchSubmit} className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search description or unit"
            className="pl-8 rounded-sm h-10 w-[280px]"
            data-testid="md-search"
          />
        </form>
        <div className="ml-auto flex flex-wrap gap-2">
          <a
            href={`${API}/master-descriptions/template.csv`}
            className="inline-flex items-center gap-1.5 h-10 px-3 text-sm border border-neutral-300 rounded-sm hover:border-primary hover:text-primary transition-colors"
            data-testid="md-template-csv"
            target="_blank"
            rel="noopener noreferrer"
          >
            <DownloadSimple size={14} /> Template
          </a>
          <a
            href={`${API}/master-descriptions/export.xlsx?project_id=${projectId}`}
            className="inline-flex items-center gap-1.5 h-10 px-3 text-sm border border-neutral-300 rounded-sm hover:border-primary hover:text-primary transition-colors"
            data-testid="md-export-xlsx"
            target="_blank"
            rel="noopener noreferrer"
          >
            <DownloadSimple size={14} /> Export (.xlsx)
          </a>
          <a
            href={`${API}/master-descriptions/export.csv?project_id=${projectId}`}
            className="inline-flex items-center gap-1.5 h-10 px-3 text-sm border border-neutral-300 rounded-sm hover:border-primary hover:text-primary transition-colors"
            data-testid="md-export-csv"
            target="_blank"
            rel="noopener noreferrer"
          >
            <DownloadSimple size={14} /> Export (.csv)
          </a>
          <Button variant="outline" className="rounded-sm h-10 gap-1.5" onClick={() => setImportOpen(true)} data-testid="md-import-btn">
            <UploadSimple size={14} /> Import
          </Button>
          <Button className="rounded-sm h-10 bg-primary hover:bg-[#0039CC] gap-1.5" onClick={() => setDialog({ mode: "create" })} data-testid="md-add-btn">
            <Plus size={14} weight="bold" /> Add
          </Button>
        </div>
      </div>

      {/* Stats card */}
      <div className="mb-4">
        <div className="border border-neutral-200 bg-white rounded-sm p-4 inline-block min-w-[240px]">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Total Master Descriptions</div>
          <div className="font-display text-2xl font-bold tabular-nums mt-1" data-testid="md-total-count">{total}</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden" data-testid="md-table">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-neutral-200 bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
          <div className="col-span-6">Description</div>
          <div className="col-span-2">Unit</div>
          <div className="col-span-1 text-center">BOQ Tags</div>
          <div className="col-span-2 text-right">Rate</div>
          <div className="col-span-1 text-right pr-2">Actions</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-neutral-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center">
            <BookOpen size={40} className="mx-auto text-neutral-300 mb-3" />
            <div className="text-sm text-neutral-500 mb-3">No master descriptions yet for this project.</div>
            <Button className="rounded-sm bg-primary hover:bg-[#0039CC] gap-1" onClick={() => setDialog({ mode: "create" })}>
              <Plus size={14} /> Add your first entry
            </Button>
          </div>
        ) : (
          rows.map((r) => {
            const tagCount = Array.isArray(r.boq_node_ids) ? r.boq_node_ids.length : 0;
            return (
              <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-neutral-100 hover:bg-neutral-50/70" data-testid={`md-row-${r.id}`}>
                <div className="col-span-6 self-center text-sm text-neutral-800 truncate">{r.description}</div>
                <div className="col-span-2 self-center text-sm text-neutral-500">{r.unit}</div>
                <div className="col-span-1 self-center text-center">
                  {tagCount > 0 ? (
                    <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary tabular-nums" data-testid={`md-tag-count-${r.id}`}>
                      {tagCount}
                    </span>
                  ) : (
                    <span className="text-[10px] text-neutral-300">—</span>
                  )}
                </div>
                <div className="col-span-2 self-center text-right tabular-nums text-sm text-neutral-900">{fmtINR(r.rate)}</div>
                <div className="col-span-1 flex items-center justify-end gap-1 self-center">
                  <button className="p-1 text-neutral-400 hover:text-primary" onClick={() => setDialog({ mode: "edit", row: r })} data-testid={`md-edit-${r.id}`} title="Edit">
                    <PencilSimple size={14} />
                  </button>
                  <button className="p-1 text-neutral-400 hover:text-[#C5221F]" onClick={() => handleDelete(r)} data-testid={`md-delete-${r.id}`} title="Delete">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-neutral-500">
          <div>Page {page} of {totalPages} · {total} total</div>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="md-prev">Previous</Button>
            <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="md-next">Next</Button>
          </div>
        </div>
      )}

      {dialog && (
        <MasterDescriptionDialog
          projectId={projectId}
          config={dialog}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); }}
        />
      )}

      <MasterDescriptionImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        projectId={projectId}
        onImported={load}
      />
    </div>
  );
}

/* -------- Add/Edit dialog -------- */
function MasterDescriptionDialog({ projectId, config, onClose, onSaved }) {
  const isEdit = config.mode === "edit";
  const [form, setForm] = useState(() => isEdit ? {
    description: config.row.description || "",
    unit: config.row.unit || "",
    rate: config.row.rate ?? "",
    boq_node_ids: Array.isArray(config.row.boq_node_ids) ? config.row.boq_node_ids : [],
  } : { description: "", unit: "", rate: "", boq_node_ids: [] });
  const [saving, setSaving] = useState(false);
  const [tree, setTree] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);

  // Load BOQ tree so admin can tag this description to one or more nodes.
  useEffect(() => {
    if (!projectId) return;
    setTreeLoading(true);
    api.get("/boq/tree", { params: { project_id: projectId } })
      .then((r) => setTree(r.data?.nodes || []))
      .catch(() => setTree([]))
      .finally(() => setTreeLoading(false));
  }, [projectId]);

  const toggleNode = (id) => {
    setForm((f) => {
      const set = new Set(f.boq_node_ids || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...f, boq_node_ids: Array.from(set) };
    });
  };

  const submit = async () => {
    if (!form.description?.trim() || !form.unit?.trim() || form.rate === "" || form.rate === null) {
      toast.error("All fields are required");
      return;
    }
    const payload = {
      description: form.description,
      unit: form.unit,
      rate: Number(form.rate),
      boq_node_ids: form.boq_node_ids || [],
    };
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/master-descriptions/${config.row.id}`, payload);
        toast.success("Updated");
        if (data.rate_sync_available && data.matching_boq_items_count > 0) {
          const ok = window.confirm(
            `Rate updated from ₹${data.previous_rate} to ₹${data.rate}.\n\n` +
            `${data.matching_boq_items_count} matching BOQ item(s) share this description.\n\n` +
            `Also update their rates (and total amounts) to ₹${data.rate}?`
          );
          if (ok) {
            try {
              const { data: sync } = await api.post(`/master-descriptions/${config.row.id}/sync-boq-rate`);
              toast.success(`Synced rate to ${sync.updated_count} BOQ item(s)`);
            } catch (err) {
              toast.error("Rate sync failed: " + (formatApiErrorDetail(err.response?.data?.detail) || err.message));
            }
          }
        }
      } else {
        await api.post("/master-descriptions", { project_id: projectId, ...payload });
        toast.success("Created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const selectedCount = (form.boq_node_ids || []).length;

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="md-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? "Edit" : "Add"} Master Description</DialogTitle>
          <DialogDescription>
            Attach this description to one or more BOQ nodes (block / floor / item). When someone raises a PO on any
            leaf item under those nodes, this description becomes a picker option in the line-item.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Description *</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="OPC 53 Grade — 50kg bag" className="rounded-sm min-h-[70px]" data-testid="md-form-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Unit *</Label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="bags, kg, sqft…" className="rounded-sm h-10" data-testid="md-form-unit" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Rate *</Label>
              <Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="rounded-sm h-10 tabular-nums" data-testid="md-form-rate" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500 flex items-center justify-between">
              <span>BOQ Nodes ({selectedCount} selected)</span>
              {selectedCount > 0 && (
                <button
                  type="button"
                  className="text-[10px] text-primary hover:underline"
                  onClick={() => setForm((f) => ({ ...f, boq_node_ids: [] }))}
                  data-testid="md-form-boq-clear"
                >Clear</button>
              )}
            </Label>
            <div className="rounded-sm border border-neutral-200 bg-neutral-50 max-h-64 overflow-y-auto p-2" data-testid="md-form-boq-tree">
              {treeLoading ? (
                <div className="text-xs text-neutral-500 px-2 py-3">Loading BOQ tree…</div>
              ) : tree.length === 0 ? (
                <div className="text-xs text-neutral-500 px-2 py-3">No BOQ nodes in this project yet.</div>
              ) : (
                <MDBoqTree nodes={tree} selected={form.boq_node_ids} onToggle={toggleNode} />
              )}
            </div>
            <p className="text-[11px] text-neutral-500 mt-1">
              Tag at any level. E.g. tagging the <b>Cement</b> block will offer this variant on all cement leaf items across floors.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="rounded-sm bg-primary hover:bg-[#0039CC]" onClick={submit} disabled={saving} data-testid="md-form-save">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MDBoqTree({ nodes, selected, onToggle }) {
  const sel = new Set(selected || []);
  // Flatten the tree into an ordered list with depth — avoids recursive JSX
  // that CRA's react-refresh transform can choke on for deeply nested trees.
  const flat = [];
  const walk = (list, depth) => {
    for (const n of list) {
      flat.push({ n, depth });
      if (n.children && n.children.length) walk(n.children, depth + 1);
    }
  };
  walk(nodes || [], 0);
  return (
    <div className="space-y-0.5">
      {flat.map(({ n, depth }) => (
        <label
          key={n.id}
          className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-white cursor-pointer text-xs"
          style={{ paddingLeft: 8 + depth * 16 }}
          data-testid={`md-boq-node-${n.id}`}
        >
          <input
            type="checkbox"
            checked={sel.has(n.id)}
            onChange={() => onToggle(n.id)}
            className="rounded-sm"
          />
          <span className={`uppercase text-[9px] tracking-wider px-1.5 py-0.5 rounded-sm ${
            n.node_type === "block" ? "bg-primary/10 text-primary" :
            n.node_type === "floor" ? "bg-amber-100 text-amber-800" :
            "bg-neutral-200 text-neutral-700"
          }`}>{n.node_type}</span>
          <span className="text-neutral-800 truncate">{n.name}</span>
          {n.unit && <span className="text-neutral-400 text-[10px]">({n.unit})</span>}
        </label>
      ))}
    </div>
  );
}

/* -------- Bulk import dialog -------- */
function MasterDescriptionImportDialog({ open, onOpenChange, projectId, onImported }) {
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) { setFile(null); setReplace(false); setResult(null); setBusy(false); }
  }, [open]);

  const doImport = async () => {
    if (!file) { toast.error("Choose a CSV or Excel file"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("project_id", projectId);
      form.append("replace", replace ? "true" : "false");
      const { data } = await api.post("/master-descriptions/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.inserted > 0) {
        toast.success(`${data.inserted} row(s) imported`);
        onImported?.();
      } else {
        toast.error("No rows imported — check errors below");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="md-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><UploadSimple size={18} /> Import Master Descriptions</DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file. Required columns (case-insensitive): <span className="font-medium">Description</span>, <span className="font-medium">Unit</span>, <span className="font-medium">Rate</span>.
            Categories must exist in the master categories list.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-neutral-200 rounded-sm p-8 text-center hover:border-primary transition-colors">
              <UploadSimple size={36} className="mx-auto text-neutral-400 mb-3" />
              <input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" id="md-file-input" data-testid="md-import-file" />
              <label htmlFor="md-file-input" className="cursor-pointer text-primary font-medium underline">
                {file ? file.name : "Click to select .csv or .xlsx"}
              </label>
              <div className="text-xs text-neutral-400 mt-1">Max 10 MB · <a href={`${API}/master-descriptions/template.csv`} target="_blank" rel="noopener noreferrer" className="text-primary underline">Download template</a></div>
            </div>
            <label className="flex items-center gap-2 text-xs text-neutral-600">
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="md-import-replace" />
              Replace all existing master descriptions in this project first (destructive)
            </label>
            <DialogFooter>
              <Button variant="ghost" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button className="rounded-sm bg-primary hover:bg-[#0039CC] gap-1.5" disabled={!file || busy} onClick={doImport} data-testid="md-import-confirm">
                {busy ? "Importing…" : "Import"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {result && (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center gap-3 text-sm">
              <CheckCircle size={22} weight="fill" className={result.inserted > 0 ? "text-[#137333]" : "text-neutral-400"} />
              <div>
                <div className="font-medium" data-testid="md-import-result-inserted">
                  {result.inserted} inserted · {result.skipped_invalid} skipped invalid · {result.total_rows} total
                </div>
                <div className="text-xs text-neutral-500">{result.replace ? "Previous descriptions were replaced." : "Existing rows preserved."}</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="border border-[#FCE39E] bg-[#FEF7E0] rounded-sm p-3">
                <div className="text-xs font-medium text-[#B06000] mb-2">Errors ({result.errors.length}):</div>
                <div className="text-[11px] text-neutral-700 space-y-1.5 max-h-[300px] overflow-y-auto" data-testid="md-import-errors">
                  {result.errors.map((e, i) => (
                    <div key={i} className="border-b border-[#FCE39E] pb-1.5 last:border-0">
                      <span className="font-medium">Row {e.row}:</span>{" "}
                      <span className="text-[#C5221F]">{e.reasons.join("; ")}</span>
                      <div className="text-neutral-500 text-[10px] mt-0.5">
                        [Desc: {e.raw?.description || "—"}, Unit: {e.raw?.unit || "—"}, Rate: {e.raw?.rate || "—"}]
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setResult(null)}>Import Another</Button>
              <Button className="rounded-sm bg-primary hover:bg-[#0039CC]" onClick={() => onOpenChange(false)} data-testid="md-import-done">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
 * Master Description Picker (used inside BOQ Add-Item dialog)
 * ============================================================ */
function MasterDescriptionPicker({ projectId, onPick }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    const t = setTimeout(() => {
      setLoading(true);
      const params = { project_id: projectId, page: 1, page_size: 20 };
      if (search) params.search = search;
      api.get("/master-descriptions", { params })
        .then(({ data }) => setRows(data.items || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [open, search, projectId]);

  return (
    <div className="border border-primary/30 bg-primary/5 rounded-sm p-3" data-testid="md-picker">
      <div className="flex items-center gap-2 mb-2">
        <BookOpen size={14} className="text-primary" weight="fill" />
        <span className="text-[11px] font-medium text-primary uppercase tracking-wider">Pick from Master Descriptions</span>
        <button
          type="button"
          className="ml-auto text-[11px] text-neutral-500 hover:text-primary underline"
          onClick={() => setOpen((v) => !v)}
          data-testid="md-picker-toggle"
        >
          {open ? "Hide" : "Browse Library"}
        </button>
      </div>
      {open && (
        <div className="space-y-2">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search description or unit…"
              className="pl-7 rounded-sm h-9 text-sm"
              data-testid="md-picker-search"
            />
          </div>
          <div className="max-h-[220px] overflow-y-auto border border-neutral-200 rounded-sm bg-white">
            {loading ? (
              <div className="p-4 text-center text-xs text-neutral-400">Searching…</div>
            ) : rows.length === 0 ? (
              <div className="p-4 text-center text-xs text-neutral-400">No master descriptions found. Add some in the Master Descriptions tab.</div>
            ) : (
              rows.map((r) => (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => { onPick(r); setOpen(false); }}
                  className="w-full text-left px-3 py-2 border-b border-neutral-100 hover:bg-primary/5 last:border-0"
                  data-testid={`md-picker-row-${r.id}`}
                >
                  <div className="text-sm text-neutral-900 truncate">{r.description}</div>
                  <div className="text-[11px] text-neutral-500 flex gap-2 mt-0.5">
                    <span>{r.unit}</span>
                    <span className="ml-auto tabular-nums">{fmtINR(r.rate)}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
 * Quick PO from BOQ (slim modal)
 * ============================================================ */
function QuickPOFromBOQDialog({ item, onClose, onCreated }) {
  const { activeProject } = useProject();
  const [vendors, setVendors] = useState([]);
  const [variants, setVariants] = useState([]);
  const [selected, setSelected] = useState({
    description: item.description || item.name || "",
    unit: item.unit || "nos",
    rate: Number(item.rate) || 0,
  });
  const [form, setForm] = useState({
    vendor_id: "",
    quantity: 1,
    tax_percent: 18,
    delivery_date: "",
    site_location: activeProject?.address || "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/vendors", { params: { page_size: 200 } })
      .then((v) => setVendors(Array.isArray(v.data) ? v.data : (v.data?.items || [])))
      .catch(() => {});
    // Load master-description variants attached to this BOQ item (or any ancestor).
    api.get("/master-descriptions/for-boq-item", {
      params: { boq_item_id: item.id, project_id: item.project_id },
    })
      .then((r) => setVariants(r.data?.items || []))
      .catch(() => setVariants([]));
  }, [item.id, item.project_id]);

  const rate = Number(selected.rate) || 0;
  const qty = Number(form.quantity) || 0;
  const taxPct = Number(form.tax_percent) || 0;
  const subtotal = +(rate * qty).toFixed(2);
  const taxAmount = +((subtotal * taxPct) / 100).toFixed(2);
  const grand = +(subtotal + taxAmount).toFixed(2);
  const canSubmit = !!form.vendor_id && qty > 0 && !saving;

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Vendor and Quantity are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        vendor_id: form.vendor_id,
        contract_type: "",
        site_location: form.site_location,
        delivery_date: form.delivery_date,
        notes: form.notes,
        terms: "Payment within 30 days of delivery. Goods to comply with specifications.",
        po_type: "material",
        project_id: item.project_id,
        boq_item_id: item.id,
        line_items: [{
          description: selected.description,
          quantity: qty,
          unit: selected.unit || "nos",
          unit_price: rate,
          tax_percent: taxPct,
          total: grand,
        }],
        subtotal, tax_amount: taxAmount, total_amount: grand,
      };
      const { data } = await api.post("/pos", payload);
      toast.success(`PO ${data.po_number} raised for ${item.name} — visit Material POs to view`);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const pickVariant = (desc) => {
    const v = variants.find((x) => x.description === desc);
    if (v) {
      setSelected({
        description: v.description,
        unit: v.unit || selected.unit,
        rate: Number(v.rate) || 0,
      });
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="quick-po-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Plus size={16} weight="bold" /> Raise PO — {item.name}
          </DialogTitle>
          <DialogDescription>
            Description, Unit and Rate come from the BOQ. Pick a linked variant if available, then set Vendor + Quantity.
          </DialogDescription>
        </DialogHeader>

        {/* Locked panel */}
        <div className="bg-primary/5 border border-primary/30 rounded-sm p-3 space-y-1.5 text-sm">
          <div className="flex items-start gap-2">
            <span className="text-[10px] uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm mt-0.5">BOQ</span>
            <div className="flex-1">
              <div className="text-neutral-900 font-medium" data-testid="quick-po-selected-desc">{selected.description}</div>
              <div className="text-[11px] text-neutral-500">Unit: {selected.unit} · Rate: {fmtINR(rate)} (locked)</div>
            </div>
          </div>
          {variants.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 block">
                Pick a linked variant ({variants.length})
              </Label>
              <Select
                value={variants.find((v) => v.description === selected.description) ? selected.description : ""}
                onValueChange={pickVariant}
              >
                <SelectTrigger className="rounded-sm h-9 bg-white text-xs" data-testid="quick-po-variant-select">
                  <SelectValue placeholder="Choose a variant description" />
                </SelectTrigger>
                <SelectContent>
                  {variants.map((v) => (
                    <SelectItem key={v.id} value={v.description} data-testid={`quick-po-variant-${v.id}`}>
                      <div className="flex flex-col">
                        <span>{v.description}</span>
                        <span className="text-[10px] text-neutral-500">{v.unit} · ₹{v.rate}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Vendor *</Label>
            <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
              <SelectTrigger className="rounded-sm h-10" data-testid="quick-po-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
              <SelectContent className="max-h-[280px]">
                {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Quantity *</Label>
              <Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="rounded-sm h-10 tabular-nums" data-testid="quick-po-qty" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Tax %</Label>
              <Input type="number" step="0.01" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} className="rounded-sm h-10 tabular-nums" data-testid="quick-po-tax" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Delivery Date</Label>
              <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="rounded-sm h-10" data-testid="quick-po-delivery" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Site Location</Label>
              <Input
                value={form.site_location}
                onChange={(e) => setForm({ ...form, site_location: e.target.value })}
                placeholder="Auto-filled from project address"
                className="rounded-sm h-10"
                data-testid="quick-po-site"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" className="rounded-sm min-h-[60px]" data-testid="quick-po-notes" />
          </div>
          <div className="bg-neutral-50 border border-neutral-200 rounded-sm p-3 text-sm">
            <div className="flex justify-between text-neutral-600"><span>Subtotal</span><span className="tabular-nums">{fmtINR(subtotal)}</span></div>
            <div className="flex justify-between text-neutral-600"><span>Tax ({taxPct}%)</span><span className="tabular-nums">{fmtINR(taxAmount)}</span></div>
            <div className="flex justify-between border-t border-neutral-200 mt-1.5 pt-1.5 font-medium text-neutral-900"><span>Total</span><span className="tabular-nums" data-testid="quick-po-total">{fmtINR(grand)}</span></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" className="rounded-sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="rounded-sm bg-primary hover:bg-[#0039CC]" onClick={submit} disabled={!canSubmit} data-testid="quick-po-submit">
            {saving ? "Raising…" : "Raise PO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
 * Request Rate Revision dialog
 * ============================================================ */
function RateRevisionDialog({ item, onClose, onSubmitted }) {
  const currentRate = Number(item.rate) || 0;
  const [form, setForm] = useState({ proposed_rate: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const proposed = Number(form.proposed_rate) || 0;
  const delta = proposed - currentRate;
  const deltaPct = currentRate > 0 ? (delta / currentRate) * 100 : 0;
  const canSubmit = form.proposed_rate !== "" && proposed >= 0 && proposed !== currentRate && form.reason.trim().length >= 3 && !saving;

  const submit = async () => {
    if (!canSubmit) {
      if (proposed === currentRate) toast.error("Proposed rate is same as current rate");
      else if (form.reason.trim().length < 3) toast.error("Reason is required (min 3 chars)");
      else toast.error("Please fill Proposed Rate and Reason");
      return;
    }
    setSaving(true);
    try {
      await api.post("/rate-revisions", {
        boq_item_id: item.id,
        proposed_rate: proposed,
        reason: form.reason.trim(),
      });
      toast.success("Rate revision request submitted · Admin has been notified");
      onSubmitted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="rate-revision-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Request Rate Revision — {item.name}</DialogTitle>
          <DialogDescription>
            Submit a proposed rate change for this BOQ item. An admin will review the request; on approval the BOQ item&apos;s rate is updated automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-neutral-50 border border-neutral-200 rounded-sm p-3 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-neutral-500">Description</span><span className="text-neutral-900 truncate max-w-[60%] text-right">{item.description || item.name}</span></div>
          <div className="flex justify-between"><span className="text-neutral-500">Unit</span><span className="text-neutral-900">{item.unit || "—"}</span></div>
          <div className="flex justify-between"><span className="text-neutral-500">Current Rate</span><span className="text-neutral-900 tabular-nums" data-testid="rate-current">{fmtINR(currentRate)}</span></div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Proposed Rate *</Label>
            <Input type="number" step="0.01" min={0} value={form.proposed_rate} onChange={(e) => setForm({ ...form, proposed_rate: e.target.value })} placeholder="0.00" className="rounded-sm h-10 tabular-nums" data-testid="rate-proposed" />
            {form.proposed_rate !== "" && proposed !== currentRate && (
              <div className={"text-[11px] mt-1 tabular-nums " + (delta > 0 ? "text-[#B06000]" : "text-[#137333]")} data-testid="rate-delta">
                Δ {delta > 0 ? "+" : ""}{fmtINR(delta)} ({deltaPct > 0 ? "+" : ""}{deltaPct.toFixed(1)}%)
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Reason *</Label>
            <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why should this rate change? (vendor quote, market shift, spec change…)" className="rounded-sm min-h-[90px]" data-testid="rate-reason" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" className="rounded-sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="rounded-sm bg-primary hover:bg-[#0039CC]" onClick={submit} disabled={!canSubmit} data-testid="rate-submit">
            {saving ? "Submitting…" : "Submit Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

